import type {
  CreateOpContract,
  OpContractAccrual,
  OpContractAccrualStatus,
  OpContractExemptionBasis,
  PeriodMaster,
  Vendor,
} from '@dcc-erp/shared';

// 'YYYYMM' -> 'MM/YYYY' fallback label when no Period master row matches.
export function formatPeriodLabel(period: string, periods: PeriodMaster[]): string {
  const match = periods.find((p) => p.period === period);
  if (match) return match.nameTh;
  return `${period.slice(4, 6)}/${period.slice(0, 4)}`;
}

export type RunHistoryStatus = 'POSTED' | 'REVERSED' | 'MIXED';

export interface RunHistoryRow {
  period: string;
  label: string;
  count: number;
  postedCount: number;
  reversedCount: number;
  totalAmount: number;
  postedAt: string | null;
  postedBy: string | null;
  status: RunHistoryStatus;
}

// Collapse accrual rows into one summary row per period (newest period first).
export function groupAccrualsByPeriod(
  accruals: OpContractAccrual[],
  periods: PeriodMaster[],
): RunHistoryRow[] {
  const byPeriod = new Map<string, OpContractAccrual[]>();
  for (const a of accruals) {
    const list = byPeriod.get(a.period);
    if (list) list.push(a);
    else byPeriod.set(a.period, [a]);
  }
  const rows: RunHistoryRow[] = [];
  for (const [period, list] of byPeriod) {
    const postedCount = list.filter((a) => a.status === 'POSTED').length;
    const reversedCount = list.filter((a) => a.status === 'REVERSED').length;
    const totalAmount = list.reduce((s, a) => s + Number(a.amount), 0);
    let postedAt: string | null = null;
    let postedBy: string | null = null;
    for (const a of list) {
      const v = a.postedAt ? String(a.postedAt) : null;
      if (v && (!postedAt || v > postedAt)) {
        postedAt = v;
        postedBy = a.postedBy ?? null;
      }
    }
    postedBy ??= list.find((a) => a.postedBy)?.postedBy ?? null;
    const status: RunHistoryStatus =
      reversedCount === list.length ? 'REVERSED' : postedCount === list.length ? 'POSTED' : 'MIXED';
    rows.push({
      period,
      label: formatPeriodLabel(period, periods),
      count: list.length,
      postedCount,
      reversedCount,
      totalAmount,
      postedAt,
      postedBy,
      status,
    });
  }
  rows.sort((a, b) => (a.period < b.period ? 1 : a.period > b.period ? -1 : 0));
  return rows;
}

// Re-export the accrual status type so consumers import from one place.
export type { OpContractAccrualStatus };

export function buildAccrualStatusMap(
  accruals: OpContractAccrual[],
): Map<string, OpContractAccrualStatus> {
  const m = new Map<string, OpContractAccrualStatus>();
  for (const a of accruals) m.set(`${a.contractId}|${a.period}`, a.status);
  return m;
}

export type CellState = 'posted' | 'reversed' | 'current' | 'pending' | 'out';

// Month is 0-based. `contract.apiId` is the server contract id used as the
// accrual map key; `start`/`end` are ISO date strings. `currentOpenPeriod` is
// the 'YYYYMM' code of the company's current OPEN period (or '' if none).
export function cellStatus(
  contract: { apiId?: string | undefined; start: string; end: string },
  month: number,
  year: number,
  statusMap: Map<string, OpContractAccrualStatus>,
  currentOpenPeriod: string,
): CellState {
  // Parse the 'YYYY-MM-DD' strings by slicing rather than new Date(), which
  // would parse as UTC midnight and shift the month in zones behind UTC (the
  // same trap buildCreateContractPayload avoids). startM/endM are 0-based.
  const startY = Number(contract.start.slice(0, 4));
  const startM = Number(contract.start.slice(5, 7)) - 1;
  const endY = Number(contract.end.slice(0, 4));
  const endM = Number(contract.end.slice(5, 7)) - 1;
  const inRange =
    (year > startY || (year === startY && month >= startM)) &&
    (year < endY || (year === endY && month <= endM));
  if (!inRange) return 'out';
  const period = `${year}${String(month + 1).padStart(2, '0')}`;
  const status = contract.apiId ? statusMap.get(`${contract.apiId}|${period}`) : undefined;
  if (status === 'POSTED') return 'posted';
  if (status === 'REVERSED') return 'reversed';
  if (period === currentOpenPeriod) return 'current';
  return 'pending';
}

// 'YYYY-MM-DD' -> 'YYYYMM'.
function periodCodeOf(iso: string): string {
  return iso.slice(0, 7).replace('-', '');
}

// Default the monthly-run period to the current OPEN period, else the latest
// OPEN period, else '' (caller disables the run when ''). Mirrors the journal
// defaultVoucherDate intent but returns a period code, not a date.
export function pickDefaultPeriod(periods: PeriodMaster[], todayIso: string): string {
  const open = periods.filter((p) => p.status === 'OPEN');
  if (open.length === 0) return '';
  const todayCode = periodCodeOf(todayIso);
  if (open.some((p) => p.period === todayCode)) return todayCode;
  return open.map((p) => p.period).sort()[open.length - 1] ?? '';
}

// ============================================================
// M4 TFRS 16 — exemption basis (why a contract is NOT capitalized as a
// right-of-use asset). The 3 options + Thai labels drive the create-form picker;
// `null`/`undefined` = unclassified (legacy/pre-M4 rows).
// ============================================================

export const EXEMPTION_BASES: readonly {
  value: OpContractExemptionBasis;
  label: string;
}[] = [
  { value: 'SHORT_TERM', label: 'สัญญาสั้น ≤12 เดือน' },
  { value: 'LOW_VALUE', label: 'สินทรัพย์มูลค่าต่ำ' },
  { value: 'SERVICE_NON_LEASE', label: 'สัญญาบริการ (ไม่ใช่สัญญาเช่า)' },
];

// A short-term lease by definition has a term ≤12 months; if its per-contract
// term exceeds 12 it can no longer use the short-term exemption and must be
// reassessed for capitalization. Pure mirror of the server-derived flag.
export function needsReclassification(
  exemptionBasis: OpContractExemptionBasis | null | undefined,
  monthsTotal: number,
): boolean {
  return exemptionBasis === 'SHORT_TERM' && monthsTotal > 12;
}

export interface NewContractForm {
  contractType: string;
  startDate: string;
  months: number;
  monthly: number;
  expenseAccountCode: string;
  vendorId?: string;
  branchId?: string;
  departmentId?: string;
  notes?: string;
  noticePeriodDays?: number | null;
  autoRenew?: boolean;
  exemptionBasis?: OpContractExemptionBasis | null;
}

// endDate = last day of (startMonth + months). Includes optional dims/notes only
// when non-empty so we never send '' for a uuid field the API would reject.
export function buildCreateContractPayload(f: NewContractForm): CreateOpContract {
  const start = new Date(f.startDate);
  // Use local year/month/day to avoid UTC-offset shifting the date string.
  const end = new Date(start.getFullYear(), start.getMonth() + f.months, 0);
  const endDate = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
  const notes = f.notes?.trim();
  return {
    contractType: f.contractType,
    startDate: f.startDate,
    endDate,
    monthlyAmount: f.monthly,
    expenseAccountCode: f.expenseAccountCode,
    autoAccrual: true,
    autoRenew: f.autoRenew ?? false,
    ...(f.noticePeriodDays != null ? { noticePeriodDays: f.noticePeriodDays } : {}),
    ...(f.vendorId ? { vendorId: f.vendorId } : {}),
    ...(f.branchId ? { branchId: f.branchId } : {}),
    ...(f.departmentId ? { departmentId: f.departmentId } : {}),
    ...(notes ? { notes } : {}),
    ...(f.exemptionBasis ? { exemptionBasis: f.exemptionBasis } : {}),
  };
}

export function vendorOptions(vendors: Vendor[]): { value: string; label: string }[] {
  return vendors.map((v) => ({ value: v.id, label: `${v.code} — ${v.nameTh}` }));
}

// 'YYYY-MM-DD' + N months → last day of the resulting month, as 'YYYY-MM-DD'.
// Used to seed a renewal's default endDate from its startDate + term. Mirrors
// buildCreateContractPayload's local-date math to avoid a UTC month shift.
export function addMonthsLastDay(startIso: string, months: number): string {
  const y = Number(startIso.slice(0, 4));
  const m = Number(startIso.slice(5, 7)) - 1;
  const end = new Date(y, m + months, 0);
  return `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
}

export type NoticeTier = 'expired' | 'd7' | 'd30' | 'd60' | 'd90' | 'ok';

export interface NoticeInfo {
  daysLeft: number;
  noticeDate: string | null;
  noticeDaysLeft: number | null;
  tier: NoticeTier;
  willAutoRenew: boolean;
}

function daysBetweenIso(fromIso: string, toIso: string): number {
  const a = Date.UTC(+fromIso.slice(0, 4), +fromIso.slice(5, 7) - 1, +fromIso.slice(8, 10));
  const b = Date.UTC(+toIso.slice(0, 4), +toIso.slice(5, 7) - 1, +toIso.slice(8, 10));
  return Math.round((b - a) / 86_400_000);
}

function addDaysIso(iso: string, delta: number): string {
  const d = new Date(Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10) + delta));
  return d.toISOString().slice(0, 10);
}

export function noticeInfo(
  c: {
    endDate: string;
    noticePeriodDays?: number | null | undefined;
    autoRenew?: boolean | null | undefined;
  },
  todayIso: string,
): NoticeInfo {
  const end = c.endDate.slice(0, 10);
  const daysLeft = daysBetweenIso(todayIso, end);
  const noticeDate =
    c.noticePeriodDays && c.noticePeriodDays > 0 ? addDaysIso(end, -c.noticePeriodDays) : null;
  const noticeDaysLeft = noticeDate ? daysBetweenIso(todayIso, noticeDate) : null;
  const tier: NoticeTier =
    daysLeft < 0
      ? 'expired'
      : daysLeft <= 7
        ? 'd7'
        : daysLeft <= 30
          ? 'd30'
          : daysLeft <= 60
            ? 'd60'
            : daysLeft <= 90
              ? 'd90'
              : 'ok';
  return { daysLeft, noticeDate, noticeDaysLeft, tier, willAutoRenew: !!c.autoRenew };
}

export interface Drawdown {
  total: number;
  recognized: number;
  remaining: number;
  pct: number;
}

export function contractDrawdown(
  monthly: number,
  months: number,
  accruedAmount: string | number,
): Drawdown {
  const total = monthly * months;
  const recognized = Number(accruedAmount);
  const remaining = Math.max(0, total - recognized);
  const pct = total > 0 ? Math.min(100, Math.round((recognized / total) * 100)) : 0;
  return { total, recognized, remaining, pct };
}
